<template>
    <div class="space-y-6 pb-8">
        <h1 class="-mb-4 pl-2 text-2xl font-semibold tracking-wide">
            {{ planTitle }}
        </h1>

        <plan-presentation
            v-if="!isEditing"
            :plan="planData"
            :allow-bookings="allowBookings"
        />

        <template v-if="allowBookings">
            <prime-message
                v-if="
                    contractData?.readyForPurchase ===
                    ContractReadyForPurchase.COURSE_CANCELLED
                "
                severity="warn"
                :closable="false"
            >
                {{ t('purchaseContract.courseCancelled') }}
            </prime-message>

            <duplicate-contract-message
                :plan-id="planData.id"
                :contract-id="contractData?.id"
                :warning-text="t('purchaseContract.duplicateProgramWarning')"
                :edit-text="t('purchaseContract.editBooking')"
            />

            <select-course
                :is-active-section="activeSection === 0"
                :contract="contractData"
                :plan="planData"
                :is-mutating-contract="isCreatingContract || isChangingContract"
                @create-contract="handleCreateContract"
                @change-contract="handleChangeContract"
                @edit-section="activeSection = 0"
            />

            <template v-if="contractData !== undefined">
                <participant-details
                    v-if="activeSection > 0"
                    :is-active-section="activeSection === 1"
                    :contract="contractData"
                    :plan="planData"
                    :is-editing="isEditing"
                    :is-updating-contract="
                        isUpdatingContract || isActivatingContract
                    "
                    @continue="handleContinue"
                    @add-to-cart="handleAddToCartParticipantDetails"
                    @activate-contract-now="handleActivateContractNow"
                    @edit-section="activeSection = 1"
                />

                <checkout-section
                    v-if="isKiosk && activeSection > 1"
                    :contract="contractData"
                />

                <document-section
                    v-if="!isKiosk && activeSection > 1"
                    :contract="contractData"
                    :is-signing-document="isSigningDocument"
                    :invalid-initials="invalidInitials"
                    :invalid-signatures="invalidSignatures"
                    @add-to-cart="addToCart"
                    @sign-document="handleSignDocument"
                />
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PurchaseProgramPlanFragment = graphql(`
    fragment PurchaseProgramPlanFragment on Plan {
        id
        name
        planType
        checkAllowBookings: bookingLimit(input: $input) {
            allowBookings
        }
        presentation(input: $input, language: $language) {
            title
        }
        ...PlanPresentationPlanFragment
        ...SelectCoursePlanFragment
        ...ParticipantDetailsPlanFragment
    }
`)

export const PurchaseProgramContractFragment = graphql(`
    fragment PurchaseProgramContractFragment on PlanContract {
        id
        draftExpiresAtUtc
        updatedAtUtc
        readyForPurchase(input: { checkDocument: true })
        ...SelectCourseContractFragment
        ...ParticipantDetailsContractFragment
        ...DocumentSectionContractFragment
        ...PaymentMethodInputPlanContractFragment
    }
`)
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import SelectCourse from '~/components/PurchaseContract/Program/SelectCourse.vue'
import ParticipantDetails from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetails.vue'
import DocumentSection from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import {
    setCartVisible,
    useRedirectOnContractDelete,
    useSetCartData
} from '~/composables/use-cart'
import { useRouter } from 'vue-router'
import type {
    ChangeContractInput,
    CreateContractInput,
    SignDocumentInput,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import DuplicateContractMessage from '~/components/PurchaseContract/DuplicateContractMessage.vue'
import { useRouteLink } from '~/composables/routing'
import PlanPresentation from '~/components/PurchaseContract/PlanPresentation.vue'
import { useProgramUpdateContractMutation } from '~/components/PurchaseContract/Program/use-program-update-contract-mutation'
import { useProgramSignDocumentMutation } from '~/components/PurchaseContract/Program/use-program-sign-document-mutation'
import { useProgramChangeContractMutation } from '~/components/PurchaseContract/Program/use-program-change-contract-mutation'
import { useProgramCreateContractMutation } from '~/components/PurchaseContract/Program/use-program-create-contract-mutation'
import { useActivateContractMutation } from '~/components/PurchaseContract/use-activate-contract-mutation'
import { useToast } from 'primevue/usetoast'
import CheckoutSection from '~/components/PurchaseContract/CheckoutSection/CheckoutSection.vue'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useQueryClient } from '@tanstack/vue-query'
import { useStorefrontPrefix } from '~/composables/use-storefront-prefix'
import { getEditingActiveSection } from '~/components/PurchaseContract/purchase-contract-helpers'
import { useOrgShortTitle } from '~/composables/use-org'
import { useHead } from '@unhead/vue'
import { useShowAlert } from '#imports'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { useAddToCartAnalytics } from '~/composables/analytics/add-to-cart'
import { useI18n } from 'vue-i18n'
import PrimeMessage from 'primevue/message'
import { ContractReadyForPurchase } from '../../../../resources/app/generated/enums'

const props = defineProps<{
    plan: FragmentType<typeof PurchaseProgramPlanFragment>
    contract: FragmentType<typeof PurchaseProgramContractFragment> | undefined
    isEditing: boolean
}>()

const emit = defineEmits<{
    (
        e: 'update:contract',
        payload:
            | FragmentType<typeof PurchaseProgramContractFragment>
            | undefined
    ): void
}>()

const { isKiosk } = useLayoutMode()
const { emitAddToCartEvent } = useAddToCartAnalytics()
const { t } = useI18n({ useScope: 'global' })

const planData = computed(() =>
    getFragmentData(PurchaseProgramPlanFragment, props.plan)
)

const planTitle = computed(() => {
    if (
        planData.value.presentation !== null &&
        planData.value.presentation.title !== ''
    ) {
        return planData.value.presentation.title
    }

    return planData.value.name
})

const shortTitle = useOrgShortTitle()
useHead({
    title: () => shortTitle.value + ' | ' + planTitle.value
})

const allowBookings = computed(
    () => planData.value.checkAllowBookings.allowBookings
)

const contractData = computed(() =>
    props.contract !== undefined
        ? getFragmentData(PurchaseProgramContractFragment, props.contract)
        : undefined
)

const activeSection = ref(0)
function setEditingActiveSection() {
    // If we are not editing, return
    if (contractData.value === undefined) {
        return
    }

    activeSection.value = getEditingActiveSection(
        contractData.value.draftExpiresAtUtc,
        contractData.value.readyForPurchase
    )
}
// Run our function on setup
setEditingActiveSection()

const router = useRouter()
const storefrontPrefix = useStorefrontPrefix()
const { storefrontHomeLink } = useRouteLink()
function addToCart() {
    setCartVisible(true)
    router.push(storefrontHomeLink(storefrontPrefix.value))
}

const { mutate: createContract, isPending: isCreatingContract } =
    useProgramCreateContractMutation()
const queryClient = useQueryClient()
function handleCreateContract(input: CreateContractInput) {
    createContract(input, {
        onSuccess: data => {
            emit('update:contract', data)
            activeSection.value = 1
            queryClient.invalidateQueries({
                predicate: query =>
                    query.queryKey.includes('CourseCalendarQuery')
            })
        }
    })
}

const { mutate: changeContract, isPending: isChangingContract } =
    useProgramChangeContractMutation()

function handleChangeContract(input: ChangeContractInput) {
    changeContract(input, {
        onSuccess: data => {
            if (data.record.__typename === 'PlanContract') {
                emit('update:contract', data.record)
                activeSection.value = 1
                queryClient.invalidateQueries({
                    predicate: query =>
                        query.queryKey.includes('CourseCalendarQuery')
                })
                return
            }

            throw new Error()
        }
    })
}

const { mutateAsync: updateContract, isPending: isUpdatingContract } =
    useProgramUpdateContractMutation()
const setCartData = useSetCartData()
function handleContinue(input: UpdateContractInput) {
    updateContract(input, {
        onSuccess: data => {
            if (data.record.__typename === 'PlanContract') {
                emit('update:contract', data.record)
                activeSection.value = 2
                return
            }

            throw new Error()
        }
    })
}

function handleAddToCartParticipantDetails(input: UpdateContractInput) {
    updateContract(input, {
        onSuccess: data => {
            if (data.record.__typename === 'PlanContract') {
                emitAddToCartEvent(data.record)
            }

            addToCart()
        }
    })
}

const showAlert = useShowAlert()
const toast = useToast()
const { mutate: activateContractMutate, isPending: isActivatingContract } =
    useActivateContractMutation()
async function handleActivateContractNow(input: UpdateContractInput) {
    await updateContract(input, {
        onSuccess: data => {
            if (data.record.__typename === 'PlanContract') {
                emit('update:contract', data.record)
                return
            }

            throw new Error()
        }
    })

    if (contractData.value === undefined) {
        throw new Error()
    }

    activateContractMutate(
        {
            id: contractData.value.id,
            updatedAtUtc: contractData.value.updatedAtUtc,
            documentInput: null
        },
        {
            onSuccess: data => {
                if (
                    data.__typename === 'ActivateContractResult' ||
                    data.__typename === 'ContractIsActive'
                ) {
                    toast.add({
                        severity: 'success',
                        summary: t('purchaseContract.thankYouForPurchase'),
                        detail: planTitle.value,
                        life: 10000,
                        closable: false
                    })
                    router.push(storefrontHomeLink(storefrontPrefix.value))
                } else {
                    showAlert(t('oops'), t('purchaseContract.purchaseFailed'))
                }
            }
        }
    )
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const { mutate: signDocument, isPending: isSigningDocument } =
    useProgramSignDocumentMutation()
function handleSignDocument(mutationData: SignDocumentInput) {
    signDocument(mutationData, {
        onSuccess: data => {
            if (data.signDocument.__typename === 'ValidationError') {
                handleValidationErrors(
                    data.signDocument.fieldErrors,
                    invalidInitials,
                    invalidSignatures
                )
                return
            }

            if (data.signDocument.__typename === 'SignDocumentResult') {
                // Update the cart data
                if (data.signDocument.query.viewer !== null) {
                    setCartData(data.signDocument.query.viewer.cart)
                }

                if (data.signDocument.record.__typename === 'PlanContract') {
                    emit('update:contract', data.signDocument.record)
                    return
                }
            }

            throw new Error()
        }
    })
}

watch(
    () => planData.value.id,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            // close all the components
            activeSection.value = 0
            // Remove contract data
            emit('update:contract', undefined)
        }
    }
)

useRedirectOnContractDelete(() => contractData.value?.id)
</script>
